import {
  NotificationsState,
  NotificationT,
  PersistedNotificationT,
  PersistedNotificationTypeT,
} from 'lib-shared-legacy-types';
import sortBy from 'lodash/sortBy';
import moment from 'moment-timezone';
import { createSelector } from 'reselect';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { fromNowOrNow } from 'utils/fromNowOrNow';

/**
 * Helper function to get notification type as the second argument when using
 * selectors.
 */
const selectType = (_: RootReducerStateT, type: string): string => type;

export const getNotificationsState = (
  state: RootReducerStateT,
): NotificationsState => state.notifications;

/** Selects current notifications. */
export const selectCurrentNotifications = (
  state: RootReducerStateT,
): NotificationT[] => getNotificationsState(state).currentNotifications;

export const getNotificationsHistory = (state: RootReducerStateT) =>
  getNotificationsState(state).notificationsHistory;

export const getPersistedNotifications = (state: RootReducerStateT) =>
  getNotificationsState(state).persistedNotifications;

export type AllNotificationsHistoryT = {
  type: 'local' | 'persisted';
  id: string;
  data: NotificationT;
  timestamp: number;
  message?: string;
  bulkId: null | string;
  bulkStatus: null | PersistedNotificationTypeT;
};

const transformPersistedNotification = (
  p: PersistedNotificationT,
): AllNotificationsHistoryT => {
  const {
    data: { params },
  } = p;
  const notification = {
    title: p.data.params.subject,
    text: '',
    subText: fromNowOrNow(moment(p.createdAt)),
    icon: 'sync',
    status: 'default',
  };

  if (params.notification_type === 'BulkActionSucceeded') {
    notification.icon = 'done';
    notification.status = 'success';
  } else if (params.notification_type === 'BulkActionFailed') {
    notification.status = 'danger';
    notification.icon = 'exclamation';
  } else if (params.notification_type === 'BulkActionPartialFailed') {
    notification.status = 'warning';
    notification.icon = 'exclamation';
  }

  return {
    type: 'persisted',
    id: p.notificationId,
    data: notification,
    timestamp: new Date(p.createdAt).getTime(),
    message: p.data.params.message,
    bulkId: p.data.params.bulk_id,
    bulkStatus: params.notification_type,
  } as any;
};

const transformLocalNotification = (
  notification: NotificationT,
): AllNotificationsHistoryT => {
  return {
    type: 'local',
    id: notification.id,
    data: {
      ...notification,
      text: '',
      subText: fromNowOrNow(moment(notification.timestamp)),
    },
    timestamp: new Date(notification.timestamp).getTime(),
    bulkId: null,
    bulkStatus: null,
  };
};

export const getAllNotificationHistory = createSelector(
  [getNotificationsHistory, getPersistedNotifications],
  (notificationsHistory, persistedNotifications) => {
    const persisted = persistedNotifications.map(
      transformPersistedNotification,
    );

    const local = notificationsHistory.map(transformLocalNotification);

    return sortBy(
      [...local, ...persisted],
      ['timestamp'],
    ).reverse() as AllNotificationsHistoryT[];
  },
);

/** Selects current notifications having given type. */
export const selectCurrentNotificationsByType = createSelector(
  [selectCurrentNotifications, selectType],
  (notifications: NotificationT[], type) =>
    notifications.filter((notification) => notification.type === type),
);
